import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaMapMarkerAlt, FaGlobe, FaUsers, FaFacebook, FaTwitter, FaLinkedin } from 'react-icons/fa';

const socialLinks = [
  { label: 'LinkedIn', Icon: FaLinkedin, href: 'https://www.linkedin.com/company/teztecchsolutions/posts/?feedView=all' },
  { label: 'X', Icon: FaTwitter, href: 'https://x.com/teztecch' },
  { label: 'Facebook', Icon: FaFacebook, href: 'https://www.facebook.com/teztecch.id/' },
];

const Footer = () => {
  const location = useLocation();

  if (['/login', '/register', '/messages'].includes(location.pathname)) {
    return null;
  }

  return (
    <footer className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #009e8e 0%, #00c2ab 100%)' }}>
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
      <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-[100px] pointer-events-none -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-[100px] pointer-events-none translate-x-1/2 translate-y-1/2"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 pb-12 border-b border-white/20">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                <span className="text-white font-black text-lg">N</span>
              </div>
              <span className="text-white font-extrabold text-xl tracking-tight">Naukri Platform</span>
            </div>
            <p className="text-white/80 text-sm leading-relaxed font-medium">
              Your complete platform for networking, growth, and building a better future.
            </p>
            <div className="flex items-center gap-3 mt-6">
              {socialLinks.map(({ label, Icon, href }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={`Open our ${label} page`} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/30 border border-white/20 flex items-center justify-center text-white transition-all duration-300 hover:scale-110">
                  <Icon className="text-sm" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-5">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { label: 'Browse Jobs', to: '/jobs' },
                { label: 'Companies', to: '/companies' },
                { label: 'Pricing', to: '/plans' },
                { label: 'Post a Job', to: '/post-job' },
              ].map(link => (
                <li key={link.label}>
                  <Link to={link.to} className="text-white/75 hover:text-white text-sm font-medium transition-colors duration-200 hover:underline underline-offset-4">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* For Employers */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-5">For Employers</h4>
            <ul className="space-y-3">
              {[
                { label: 'Post a Job', to: '/post-job' },
                { label: 'Employer Dashboard', to: '/manage-company' },
                { label: 'Create Company Profile', to: '/create-company' },
                { label: 'Browse Candidates', to: '/jobs' },
              ].map(link => (
                <li key={link.label}>
                  <Link to={link.to} className="text-white/75 hover:text-white text-sm font-medium transition-colors duration-200 hover:underline underline-offset-4">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-5">Contact Us</h4>
            <ul className="space-y-3 text-sm text-white/80 font-medium">
              <li className="flex items-start gap-3">
                <FaMapMarkerAlt className="mt-0.5 text-white/60 shrink-0" />
                <a href="https://www.google.com/maps/search/?api=1&query=Plot%201%2FA%2FK%2C%20Beside%20Gajanan%20Primary%20School%2C%20Ayodhya%20Nagar%2C%20Nagpur%2C%20Maharashtra%20440024" target="_blank" rel="noopener noreferrer" className="hover:text-white hover:underline underline-offset-4">Plot 1/A/K, Beside Gajanan Primary School, Ayodhya Nagar, Nagpur, Maharashtra 440024</a>
              </li>
              <li className="flex items-center gap-3">
                <FaGlobe className="text-white/60 shrink-0" />
                <a href="mailto:info@teztecch.com" className="hover:text-white hover:underline underline-offset-4">info@teztecch.com</a>
              </li>
              <li className="flex items-center gap-3">
                <FaUsers className="text-white/60 shrink-0" />
                <span>Mon - Sat: 10am - 7pm</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
          <p className="text-white/70 font-medium">© {new Date().getFullYear()} Teztecch. All rights reserved.</p>
          <div className="flex items-center gap-6 text-white/70 font-medium">
            <Link to="/about" className="hover:text-white transition-colors">About Us</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
